import React, { useState, useMemo, useEffect } from 'react';
import {
  TrendingUp,
  Ticket,
  FileDown,
  Calendar,
  MessageCircle,
  Clock,
  CheckCircle,
  Activity,
  Briefcase,
  X,
} from 'lucide-react';
import { subDays, startOfMonth, isWithinInterval, parseISO } from 'date-fns';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import toast from 'react-hot-toast';
import { useConversations } from '../../hooks/whatsapp/useChat';
import { useKanbanStore, KanbanTicket } from '../../stores/whatsapp/kanbanStore';
import type { Conversation } from '../../stores/whatsapp/chatStore';
import {
  formatDate,
  formatTime,
  formatPhone,
  getConnectionLabel,
  parseUTC,
} from '../../services/whatsapp/waFormatters';
import {
  TICKET_CATEGORIES,
  PRIORITIES,
  CONVERSATION_STATUSES,
  KANBAN_COLUMNS,
} from '../../services/whatsapp/waConstants';
import RowDetailModal from './RowDetailModal';
import WaHeatmapsTab from './WaHeatmapsTab';
import WaExecutivoTab from './WaExecutivoTab';

const COLORS = ['#007AFF', '#34C759', '#FF9500', '#FF2D55', '#AF52DE', '#5856D6', '#8E8E93'];
const tooltipStyle = {
  borderRadius: '12px',
  border: 'none',
  boxShadow: '0 4px 24px rgba(0,0,0,0.12)',
  fontSize: '13px',
};

function downloadCSV(filename: string, headers: string[], rows: string[][]) {
  const csvContent = [headers.join(';'), ...rows.map(r => r.join(';'))].join('\n');
  const blob = new Blob([`\uFEFF${csvContent}`], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

type ActiveTab = 'executivo' | 'atendimentos' | 'produtividade' | 'tickets' | 'mapas-calor';

export default function ReportsPage() {
  const { data: allConversations } = useConversations();
  const allKanbanTickets = useKanbanStore(s => s.tickets);
  const kanbanLoaded = useKanbanStore(s => s.loaded);
  const fetchKanbanTickets = useKanbanStore(s => s.fetchTickets);
  const [activeTab, setActiveTab] = useState<ActiveTab>('atendimentos');
  const [period, setPeriod] = useState<'7d' | '30d' | 'mes' | 'custom'>('mes');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');

  // Linha selecionada para detalhe (popup) em cada aba. Apenas uma por vez.
  const [detailConv, setDetailConv] = useState<Conversation | null>(null);
  const [detailTicket, setDetailTicket] = useState<KanbanTicket | null>(null);
  const [detailAgent, setDetailAgent] = useState<string | null>(null);

  // Carrega os tickets do Kanban ao montar a pagina. Sem isso, a aba "Tickets"
  // (incluindo o grafico "Por Categoria") ficava vazia para quem entrava em
  // Relatorios sem ter aberto o Kanban antes — o store so era populado la.
  useEffect(() => {
    if (!kanbanLoaded) fetchKanbanTickets();
  }, [kanbanLoaded, fetchKanbanTickets]);

  // Calcular range de datas baseado no periodo selecionado
  const dateRange = useMemo(() => {
    const now = new Date();
    if (period === '7d') return { start: subDays(now, 7), end: now };
    if (period === '30d') return { start: subDays(now, 30), end: now };
    if (period === 'mes') return { start: startOfMonth(now), end: now };
    if (period === 'custom' && customStart && customEnd) {
      return { start: parseISO(customStart), end: parseISO(customEnd) };
    }
    return null; // sem filtro
  }, [period, customStart, customEnd]);

  // Nº de dias do período selecionado — propagado para as abas Executivo e
  // Mapas de Calor (que consultam o backend por ?days=N). Sem isto, ambas
  // ficavam fixas em 30 dias e ignoravam o seletor de período.
  const periodDays = useMemo(() => {
    if (!dateRange) return 30;
    const ms = dateRange.end.getTime() - dateRange.start.getTime();
    return Math.max(1, Math.min(365, Math.round(ms / (1000 * 60 * 60 * 24))));
  }, [dateRange]);

  // Filtrar conversas e tickets pelo periodo.
  // As datas do backend (started_at/createdAt) vem em UTC — usamos parseUTC
  // (mesma conversao do waFormatters) para comparar no instante absoluto certo,
  // evitando off-by-3h em bordas de periodo (ex.: meia-noite). O dateRange usa o
  // fuso local do navegador, e isWithinInterval compara instantes absolutos.
  const conversations = useMemo(() => {
    if (!dateRange) return allConversations;
    return allConversations.filter(c => {
      const date = c.started_at ? parseUTC(c.started_at) : null;
      if (!date) return true;
      return isWithinInterval(date, { start: dateRange.start, end: dateRange.end });
    });
  }, [allConversations, dateRange]);

  // Filtra os cards do Kanban pelo periodo selecionado, usando createdAt.
  // Mesmo padrao do filtro de `conversations` acima: card sem data de criacao
  // (ex.: criado localmente antes do backend devolver) e sempre incluido.
  const kanbanTickets = useMemo(() => {
    if (!dateRange) return allKanbanTickets;
    return allKanbanTickets.filter(t => {
      const date = t.createdAt ? parseUTC(t.createdAt) : null;
      if (!date) return true;
      return isWithinInterval(date, { start: dateRange.start, end: dateRange.end });
    });
  }, [allKanbanTickets, dateRange]);

  // === DADOS ATENDIMENTOS ===
  const atendimentoData = useMemo(() => {
    const byStatus = [
      {
        name: 'Abertas',
        value: conversations.filter(c => c.status === 'open').length,
        color: '#007AFF',
      },
      {
        name: 'Aguardando',
        value: conversations.filter(c => c.status === 'waiting').length,
        color: '#FF9500',
      },
      {
        name: 'Encerradas',
        value: conversations.filter(c => c.status === 'closed').length,
        color: '#8E8E93',
      },
    ];
    const byPriority = [
      { name: 'Baixa', value: conversations.filter(c => c.priority === 'low').length },
      { name: 'Media', value: conversations.filter(c => c.priority === 'medium').length },
      { name: 'Alta', value: conversations.filter(c => c.priority === 'high').length },
      { name: 'Urgente', value: conversations.filter(c => c.priority === 'urgent').length },
    ];
    return { byStatus, byPriority, total: conversations.length };
  }, [conversations]);

  // === DADOS PRODUTIVIDADE ===
  const produtividadeData = useMemo(() => {
    const agentMap: Record<string, { total: number; closed: number }> = {};
    conversations.forEach(c => {
      const name = c.assigned_agent_name || 'Nao atribuido';
      if (!agentMap[name]) agentMap[name] = { total: 0, closed: 0 };
      agentMap[name].total += 1;
      if (c.status === 'closed') agentMap[name].closed += 1;
    });
    const agents = Object.entries(agentMap)
      .map(([nome, d]) => ({
        nome,
        atendimentos: d.total,
        resolvidos: d.closed,
        taxa: d.total > 0 ? Math.round((d.closed / d.total) * 100) : 0,
      }))
      .sort((a, b) => b.atendimentos - a.atendimentos);
    return agents;
  }, [conversations]);

  // Detalhe do agente selecionado (popup do Ranking): conversas atribuidas a ele
  // (mesmo fallback "Nao atribuido" do produtividadeData) + stats por status.
  const agentDetail = useMemo(() => {
    if (!detailAgent) return null;
    const convs = conversations.filter(
      c => (c.assigned_agent_name || 'Nao atribuido') === detailAgent,
    );
    return {
      nome: detailAgent,
      total: convs.length,
      resolvidos: convs.filter(c => c.status === 'closed').length,
      abertas: convs.filter(c => c.status === 'open').length,
      aguardando: convs.filter(c => c.status === 'waiting').length,
      taxa: convs.length
        ? Math.round((convs.filter(c => c.status === 'closed').length / convs.length) * 100)
        : 0,
      conversas: convs,
    };
  }, [detailAgent, conversations]);

  // === DADOS TICKETS ===
  const ticketData = useMemo(() => {
    const byColumn: Record<string, number> = {};
    const byCategory: Record<string, number> = {};
    const byPriority: Record<string, number> = {};
    kanbanTickets.forEach(t => {
      byColumn[t.columnId] = (byColumn[t.columnId] || 0) + 1;
      byCategory[t.category] = (byCategory[t.category] || 0) + 1;
      byPriority[t.priority] = (byPriority[t.priority] || 0) + 1;
    });
    const columnLabels: Record<string, string> = {
      backlog: 'Backlog',
      todo: 'A Fazer',
      in_progress: 'Em Andamento',
      review: 'Revisao',
      done: 'Concluido',
    };
    return {
      byColumn: Object.entries(byColumn).map(([k, v]) => ({
        name: columnLabels[k] || k,
        value: v,
      })),
      byCategory: Object.entries(byCategory).map(([k, v]) => ({ name: k, value: v })),
      byPriority: Object.entries(byPriority).map(([k, v]) => ({ name: k, value: v })),
      total: kanbanTickets.length,
    };
  }, [kanbanTickets]);

  // === EXPORTACAO ===
  const handleExport = () => {
    if (activeTab === 'atendimentos') {
      downloadCSV(
        'atendimentos.csv',
        ['Cliente', 'Telefone', 'Status', 'Prioridade', 'Agente', 'Assunto'],
        conversations.map(c => [
          c.client_name,
          c.client_phone,
          c.status,
          c.priority,
          c.assigned_agent_name || '',
          c.subject || '',
        ]),
      );
    } else if (activeTab === 'produtividade') {
      downloadCSV(
        'produtividade.csv',
        ['Agente', 'Atendimentos', 'Resolvidos', 'Taxa (%)'],
        produtividadeData.map(a => [
          a.nome,
          String(a.atendimentos),
          String(a.resolvidos),
          String(a.taxa),
        ]),
      );
    } else {
      downloadCSV(
        'tickets.csv',
        ['Titulo', 'Cliente', 'Categoria', 'Prioridade', 'Responsavel', 'Coluna'],
        kanbanTickets.map(t => [t.title, t.client, t.category, t.priority, t.assignee, t.columnId]),
      );
    }
    toast.success('CSV exportado!');
  };

  const tabs = [
    // Visão executiva (#exec): KPIs + narrativa IA + previsões ML p/ diretoria.
    { id: 'executivo' as const, label: 'Executivo', icon: Briefcase, color: '#5856D6' },
    { id: 'atendimentos' as const, label: 'Atendimentos', icon: MessageCircle, color: '#007AFF' },
    { id: 'produtividade' as const, label: 'Produtividade', icon: TrendingUp, color: '#34C759' },
    { id: 'tickets' as const, label: 'Tickets', icon: Ticket, color: '#AF52DE' },
    // Mapas de calor (#62): volume de conversas por hora/dia-semana/dia-mes.
    { id: 'mapas-calor' as const, label: 'Mapas de Calor', icon: Activity, color: '#34C759' },
  ];

  return (
    <div className="min-h-screen space-y-6 bg-[#f5f5f7] p-4 sm:p-6 lg:p-8">
      {/* Header */}
      <div className="animate-fade-in flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
        <div>
          <h1 className="text-gradient text-2xl font-bold sm:text-3xl">Análise Inteligente</h1>
          <p className="mt-1 text-sm text-gray-500">Dashboards e exportacao de dados</p>
        </div>
        <button
          type="button"
          onClick={handleExport}
          className="hover-lift flex items-center gap-2 rounded-xl bg-white px-5 py-2.5 text-sm font-medium text-gray-700 shadow-wa-apple transition-all"
        >
          <FileDown size={16} />
          Exportar CSV
        </button>
      </div>

      {/* Periodo */}
      <div className="animate-slide-up flex flex-wrap items-center gap-2">
        <div className="flex items-center overflow-hidden rounded-xl bg-white shadow-wa-apple">
          {(
            [
              ['7d', '7 dias'],
              ['30d', '30 dias'],
              ['mes', 'Este mes'],
              ['custom', 'Personalizado'],
            ] as const
          ).map(([key, label]) => (
            <button
              type="button"
              key={key}
              onClick={() => setPeriod(key)}
              className={`px-4 py-2.5 text-sm transition-colors ${period === key ? 'bg-wa-500 font-semibold text-white' : 'text-gray-600 hover:bg-white/60'}`}
            >
              {label}
            </button>
          ))}
        </div>

        {period === 'custom' && (
          <div className="flex items-center gap-2 rounded-xl bg-white px-3 py-1.5 shadow-wa-apple">
            <Calendar size={14} className="text-gray-400" />
            <input
              type="date"
              value={customStart}
              onChange={e => setCustomStart(e.target.value)}
              className="w-[130px] bg-transparent text-sm text-gray-700 outline-none"
            />
            <span className="text-gray-300">—</span>
            <input
              type="date"
              value={customEnd}
              onChange={e => setCustomEnd(e.target.value)}
              className="w-[130px] bg-transparent text-sm text-gray-700 outline-none"
            />
            {customStart && customEnd && (
              <button
                type="button"
                onClick={() => {
                  setCustomStart('');
                  setCustomEnd('');
                }}
                className="p-1 text-gray-400 hover:text-gray-600"
              >
                <X size={14} />
              </button>
            )}
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="animate-slide-up flex gap-2">
        {tabs.map(tab => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              type="button"
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 rounded-xl px-5 py-2.5 text-sm font-medium transition-all ${
                isActive
                  ? 'bg-white text-gray-900 shadow-wa-apple'
                  : 'text-gray-500 hover:bg-white/60'
              }`}
            >
              <Icon size={16} style={{ color: isActive ? tab.color : '#8E8E93' }} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* === TAB EXECUTIVO === */}
      {activeTab === 'executivo' && <WaExecutivoTab days={periodDays} />}

      {/* === TAB ATENDIMENTOS === */}
      {activeTab === 'atendimentos' && (
        <div className="animate-slide-up space-y-6">
          {/* KPI Cards */}
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            {[
              {
                label: 'Total',
                value: atendimentoData.total,
                icon: MessageCircle,
                color: '#007AFF',
                bg: 'bg-blue-50',
              },
              {
                label: 'Abertas',
                value: atendimentoData.byStatus[0].value,
                icon: Clock,
                color: '#34C759',
                bg: 'bg-green-50',
              },
              {
                label: 'Aguardando',
                value: atendimentoData.byStatus[1].value,
                icon: Clock,
                color: '#FF9500',
                bg: 'bg-orange-50',
              },
              {
                label: 'Encerradas',
                value: atendimentoData.byStatus[2].value,
                icon: CheckCircle,
                color: '#8E8E93',
                bg: 'bg-gray-50',
              },
            ].map(kpi => {
              const Icon = kpi.icon;
              return (
                <div key={kpi.label} className="rounded-2xl bg-white p-4 shadow-wa-apple">
                  <div className={`rounded-xl p-2 ${kpi.bg} mb-2 w-fit`}>
                    <Icon size={18} style={{ color: kpi.color }} />
                  </div>
                  <p className="text-2xl font-bold text-gray-900">{kpi.value}</p>
                  <p className="text-xs text-gray-500">{kpi.label}</p>
                </div>
              );
            })}
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <div className="rounded-2xl bg-white p-5 shadow-wa-apple">
              <h3 className="mb-4 text-sm font-semibold text-gray-900">Por Status</h3>
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie
                    data={atendimentoData.byStatus}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={85}
                    paddingAngle={4}
                    dataKey="value"
                  >
                    {atendimentoData.byStatus.map((entry, i) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <Cell key={i} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={tooltipStyle} />
                  <Legend wrapperStyle={{ fontSize: '12px' }} />
                </PieChart>
              </ResponsiveContainer>
            </div>

            <div className="rounded-2xl bg-white p-5 shadow-wa-apple">
              <h3 className="mb-4 text-sm font-semibold text-gray-900">Por Prioridade</h3>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={atendimentoData.byPriority}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e5ea" />
                  <XAxis dataKey="name" tick={{ fontSize: 12, fill: '#8E8E93' }} />
                  <YAxis tick={{ fontSize: 12, fill: '#8E8E93' }} />
                  <Tooltip contentStyle={tooltipStyle} />
                  <Bar dataKey="value" name="Conversas" fill="#007AFF" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Tabela detalhada */}
          <div className="overflow-hidden rounded-2xl bg-white shadow-wa-apple">
            <div className="border-b border-gray-100 px-5 py-3.5">
              <h3 className="text-sm font-semibold text-gray-900">Detalhamento de Conversas</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200/60">
                    <th className="px-5 py-3 text-left text-xs font-semibold uppercase text-gray-500">
                      Cliente
                    </th>
                    <th className="px-5 py-3 text-left text-xs font-semibold uppercase text-gray-500">
                      Status
                    </th>
                    <th className="px-5 py-3 text-left text-xs font-semibold uppercase text-gray-500">
                      Prioridade
                    </th>
                    <th className="px-5 py-3 text-left text-xs font-semibold uppercase text-gray-500">
                      Agente
                    </th>
                    <th className="px-5 py-3 text-left text-xs font-semibold uppercase text-gray-500">
                      Assunto
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {conversations.slice(0, 10).map(c => (
                    <tr
                      key={c.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => setDetailConv(c)}
                      onKeyDown={e => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setDetailConv(c);
                        }
                      }}
                      className="cursor-pointer border-b border-gray-100/60 hover:bg-gray-50/50"
                    >
                      <td className="px-5 py-3 font-medium text-gray-900">{c.client_name}</td>
                      <td className="px-5 py-3">
                        <span
                          // eslint-disable-next-line no-nested-ternary
                          className={`rounded-full px-2 py-0.5 text-xs font-semibold ${c.status === 'open' ? 'bg-blue-100 text-blue-700' : c.status === 'waiting' ? 'bg-orange-100 text-orange-700' : 'bg-gray-100 text-gray-600'}`}
                        >
                          {{ open: 'Aberto', waiting: 'Aguardando', closed: 'Fechado' }[c.status] ||
                            c.status}
                        </span>
                      </td>
                      <td className="px-5 py-3 text-gray-600">
                        {{ low: 'Baixa', medium: 'Media', high: 'Alta', urgent: 'Urgente' }[
                          c.priority
                        ] || c.priority}
                      </td>
                      <td className="px-5 py-3 text-gray-600">{c.assigned_agent_name || '—'}</td>
                      <td className="max-w-[200px] truncate px-5 py-3 text-gray-500">
                        {c.subject || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* === TAB PRODUTIVIDADE === */}
      {activeTab === 'produtividade' && (
        <div className="animate-slide-up space-y-6">
          {/* Chart */}
          <div className="rounded-2xl bg-white p-5 shadow-wa-apple">
            <h3 className="mb-4 text-sm font-semibold text-gray-900">
              Atendimentos vs Resolvidos por Agente
            </h3>
            {produtividadeData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={produtividadeData} layout="vertical" margin={{ left: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e5ea" />
                  <XAxis type="number" tick={{ fontSize: 12, fill: '#8E8E93' }} />
                  <YAxis
                    dataKey="nome"
                    type="category"
                    tick={{ fontSize: 12, fill: '#8E8E93' }}
                    width={120}
                  />
                  <Tooltip contentStyle={tooltipStyle} />
                  <Legend wrapperStyle={{ fontSize: '12px' }} />
                  <Bar
                    dataKey="atendimentos"
                    name="Atendimentos"
                    fill="#5856D6"
                    radius={[0, 6, 6, 0]}
                  />
                  <Bar
                    dataKey="resolvidos"
                    name="Resolvidos"
                    fill="#34C759"
                    radius={[0, 6, 6, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex h-[300px] items-center justify-center text-sm text-gray-400">
                Sem dados de agentes
              </div>
            )}
          </div>

          {/* Ranking */}
          <div className="overflow-hidden rounded-2xl bg-white shadow-wa-apple">
            <div className="border-b border-gray-100 px-5 py-3.5">
              <h3 className="text-sm font-semibold text-gray-900">Ranking de Produtividade</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200/60">
                    <th className="px-5 py-3 text-left text-xs font-semibold uppercase text-gray-500">
                      #
                    </th>
                    <th className="px-5 py-3 text-left text-xs font-semibold uppercase text-gray-500">
                      Agente
                    </th>
                    <th className="px-5 py-3 text-left text-xs font-semibold uppercase text-gray-500">
                      Atendimentos
                    </th>
                    <th className="px-5 py-3 text-left text-xs font-semibold uppercase text-gray-500">
                      Resolvidos
                    </th>
                    <th className="px-5 py-3 text-left text-xs font-semibold uppercase text-gray-500">
                      Taxa
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {produtividadeData.map((agent, i) => (
                    <tr
                      key={agent.nome}
                      role="button"
                      tabIndex={0}
                      onClick={() => setDetailAgent(agent.nome)}
                      onKeyDown={e => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setDetailAgent(agent.nome);
                        }
                      }}
                      className="cursor-pointer border-b border-gray-100/60 hover:bg-gray-50/50"
                    >
                      <td className="px-5 py-3 font-bold text-gray-400">{i + 1}</td>
                      <td className="px-5 py-3 font-medium text-gray-900">{agent.nome}</td>
                      <td className="px-5 py-3 text-gray-600">{agent.atendimentos}</td>
                      <td className="px-5 py-3 text-gray-600">{agent.resolvidos}</td>
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-2">
                          <div className="h-2 w-16 overflow-hidden rounded-full bg-gray-100">
                            <div
                              className="h-full rounded-full bg-[#34C759]"
                              style={{ width: `${agent.taxa}%` }}
                            />
                          </div>
                          <span className="text-xs font-semibold text-gray-700">{agent.taxa}%</span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* === TAB TICKETS === */}
      {activeTab === 'tickets' && (
        <div className="animate-slide-up space-y-6">
          {/* KPI Cards */}
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            {[
              {
                label: 'Total Tickets',
                value: ticketData.total,
                color: '#AF52DE',
                bg: 'bg-purple-50',
              },
              {
                label: 'Em Andamento',
                value: kanbanTickets.filter(t => t.columnId === 'in_progress').length,
                color: '#FF9500',
                bg: 'bg-orange-50',
              },
              {
                label: 'Concluidos',
                value: kanbanTickets.filter(t => t.columnId === 'done').length,
                color: '#34C759',
                bg: 'bg-green-50',
              },
              {
                label: 'Backlog',
                value: kanbanTickets.filter(t => t.columnId === 'backlog').length,
                color: '#8E8E93',
                bg: 'bg-gray-50',
              },
            ].map(kpi => (
              <div key={kpi.label} className="rounded-2xl bg-white p-4 shadow-wa-apple">
                <div className="mb-2 h-3 w-3 rounded-full" style={{ backgroundColor: kpi.color }} />
                <p className="text-2xl font-bold text-gray-900">{kpi.value}</p>
                <p className="text-xs text-gray-500">{kpi.label}</p>
              </div>
            ))}
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <div className="rounded-2xl bg-white p-5 shadow-wa-apple">
              <h3 className="mb-4 text-sm font-semibold text-gray-900">Por Coluna</h3>
              {ticketData.byColumn.length > 0 ? (
                <ResponsiveContainer width="100%" height={260}>
                  <PieChart>
                    <Pie
                      data={ticketData.byColumn}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={85}
                      paddingAngle={4}
                      dataKey="value"
                    >
                      {ticketData.byColumn.map((_, i) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <Cell key={i} fill={COLORS[i % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={tooltipStyle} />
                    <Legend wrapperStyle={{ fontSize: '12px' }} />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex h-[260px] items-center justify-center text-sm text-gray-400">
                  Sem tickets
                </div>
              )}
            </div>

            <div className="rounded-2xl bg-white p-5 shadow-wa-apple">
              <h3 className="mb-4 text-sm font-semibold text-gray-900">Por Categoria</h3>
              {ticketData.byCategory.length > 0 ? (
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={ticketData.byCategory}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e5ea" />
                    <XAxis dataKey="name" tick={{ fontSize: 12, fill: '#8E8E93' }} />
                    <YAxis tick={{ fontSize: 12, fill: '#8E8E93' }} />
                    <Tooltip contentStyle={tooltipStyle} />
                    <Bar dataKey="value" name="Tickets" fill="#AF52DE" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex h-[260px] items-center justify-center text-sm text-gray-400">
                  Sem tickets
                </div>
              )}
            </div>
          </div>

          {/* Tabela */}
          <div className="overflow-hidden rounded-2xl bg-white shadow-wa-apple">
            <div className="border-b border-gray-100 px-5 py-3.5">
              <h3 className="text-sm font-semibold text-gray-900">Detalhamento de Tickets</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200/60">
                    <th className="px-5 py-3 text-left text-xs font-semibold uppercase text-gray-500">
                      Titulo
                    </th>
                    <th className="px-5 py-3 text-left text-xs font-semibold uppercase text-gray-500">
                      Cliente
                    </th>
                    <th className="px-5 py-3 text-left text-xs font-semibold uppercase text-gray-500">
                      Categoria
                    </th>
                    <th className="px-5 py-3 text-left text-xs font-semibold uppercase text-gray-500">
                      Prioridade
                    </th>
                    <th className="px-5 py-3 text-left text-xs font-semibold uppercase text-gray-500">
                      Responsavel
                    </th>
                    <th className="px-5 py-3 text-left text-xs font-semibold uppercase text-gray-500">
                      Coluna
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {kanbanTickets.slice(0, 10).map(t => (
                    <tr
                      key={t.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => setDetailTicket(t)}
                      onKeyDown={e => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setDetailTicket(t);
                        }
                      }}
                      className="cursor-pointer border-b border-gray-100/60 hover:bg-gray-50/50"
                    >
                      <td className="max-w-[200px] truncate px-5 py-3 font-medium text-gray-900">
                        {t.title}
                      </td>
                      <td className="px-5 py-3 text-gray-600">{t.client}</td>
                      <td className="px-5 py-3">
                        <span className="rounded-full bg-purple-100 px-2 py-0.5 text-xs font-semibold text-purple-700">
                          {TICKET_CATEGORIES[t.category]?.label || t.category}
                        </span>
                      </td>
                      <td className="px-5 py-3 text-gray-600">
                        {PRIORITIES[t.priority]?.label || t.priority}
                      </td>
                      <td className="px-5 py-3 text-gray-600">{t.assignee || '—'}</td>
                      <td className="px-5 py-3 text-gray-500">
                        {KANBAN_COLUMNS.find(c => c.id === t.columnId)?.title || t.columnId}
                      </td>
                    </tr>
                  ))}
                  {kanbanTickets.length === 0 && (
                    <tr>
                      <td colSpan={6} className="px-5 py-8 text-center text-gray-400">
                        Nenhum ticket no Kanban
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* === TAB MAPAS DE CALOR (#62) === */}
      {activeTab === 'mapas-calor' && <WaHeatmapsTab days={periodDays} />}

      {/* Detalhe de um ticket (popup) */}
      <RowDetailModal
        isOpen={detailTicket !== null}
        onClose={() => setDetailTicket(null)}
        title={detailTicket?.title || 'Detalhes do ticket'}
        fields={
          detailTicket
            ? [
                { label: 'Título', value: detailTicket.title, full: true },
                { label: 'Cliente', value: detailTicket.client },
                {
                  label: 'Categoria',
                  value: TICKET_CATEGORIES[detailTicket.category]?.label || detailTicket.category,
                },
                {
                  label: 'Prioridade',
                  value: PRIORITIES[detailTicket.priority]?.label || detailTicket.priority,
                },
                {
                  label: 'Coluna',
                  value:
                    KANBAN_COLUMNS.find(c => c.id === detailTicket.columnId)?.title ||
                    detailTicket.columnId,
                },
                { label: 'Responsável', value: detailTicket.assignee },
                {
                  label: 'Criado em',
                  value: detailTicket.createdAt
                    ? `${formatDate(detailTicket.createdAt)} às ${formatTime(detailTicket.createdAt)}`
                    : null,
                },
                {
                  label: 'Prazo',
                  value: detailTicket.dueDate ? formatDate(detailTicket.dueDate) : null,
                },
                {
                  label: 'Horas estimadas',
                  value:
                    typeof detailTicket.estimatedHours === 'number'
                      ? `${detailTicket.estimatedHours}h`
                      : null,
                },
                { label: 'Descrição', value: detailTicket.description, full: true },
                { label: 'Resumo IA', value: detailTicket.aiSummary, full: true },
              ]
            : []
        }
      />

      {/* Detalhe de uma conversa (popup) */}
      <RowDetailModal
        isOpen={detailConv !== null}
        onClose={() => setDetailConv(null)}
        title={detailConv?.client_name || 'Detalhes da conversa'}
        fields={
          detailConv
            ? [
                { label: 'Cliente', value: detailConv.client_name },
                {
                  label: 'Telefone',
                  value: detailConv.client_phone ? formatPhone(detailConv.client_phone) : null,
                },
                { label: 'Empresa', value: detailConv.client_company },
                {
                  label: 'Status',
                  value: CONVERSATION_STATUSES[detailConv.status]?.label || detailConv.status,
                },
                {
                  label: 'Prioridade',
                  value: PRIORITIES[detailConv.priority]?.label || detailConv.priority,
                },
                { label: 'Agente', value: detailConv.assigned_agent_name },
                { label: 'Departamento', value: detailConv.department },
                { label: 'Conexão', value: getConnectionLabel(detailConv) },
                {
                  label: 'Protocolo',
                  value: detailConv.ticket_protocol || detailConv.ticket_number,
                },
                {
                  label: 'Aberta em',
                  value: detailConv.started_at
                    ? `${formatDate(detailConv.started_at)} às ${formatTime(detailConv.started_at)}`
                    : null,
                },
                {
                  label: 'Última mensagem',
                  value: detailConv.last_message_at
                    ? `${formatDate(detailConv.last_message_at)} às ${formatTime(detailConv.last_message_at)}`
                    : null,
                },
                { label: 'Não lidas', value: detailConv.unread_count || null },
                {
                  label: 'Avaliação',
                  value:
                    typeof detailConv.rating === 'number' && detailConv.rating > 0
                      ? `${detailConv.rating}/5`
                      : null,
                },
                { label: 'Assunto', value: detailConv.subject, full: true },
                { label: 'Prévia da última mensagem', value: detailConv.last_message, full: true },
                { label: 'Feedback da avaliação', value: detailConv.rating_feedback, full: true },
                { label: 'Notas internas', value: detailConv.notes, full: true },
              ]
            : []
        }
      />

      {/* Detalhe de um agente (popup do Ranking de Produtividade) */}
      <RowDetailModal
        isOpen={agentDetail !== null}
        onClose={() => setDetailAgent(null)}
        title={agentDetail?.nome || 'Detalhes do agente'}
        fields={
          agentDetail
            ? [
                { label: 'Atendimentos', value: agentDetail.total },
                { label: 'Resolvidos', value: agentDetail.resolvidos },
                { label: 'Taxa de resolução', value: `${agentDetail.taxa}%` },
                { label: 'Abertas', value: agentDetail.abertas },
                { label: 'Aguardando', value: agentDetail.aguardando },
              ]
            : []
        }
      >
        {agentDetail && (
          <div>
            <h4 className="mb-3 text-sm font-semibold text-gray-700">
              Conversas do agente ({agentDetail.conversas.length})
            </h4>
            {agentDetail.conversas.length > 0 ? (
              <div className="overflow-x-auto rounded-xl border border-gray-100">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200/60 bg-gray-50/50">
                      <th className="px-4 py-2 text-left text-xs font-semibold uppercase text-gray-500">
                        Cliente
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-semibold uppercase text-gray-500">
                        Status
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-semibold uppercase text-gray-500">
                        Prioridade
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-semibold uppercase text-gray-500">
                        Assunto
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {agentDetail.conversas.map(c => (
                      <tr key={c.id} className="border-b border-gray-100/60">
                        <td className="px-4 py-2 font-medium text-gray-900">{c.client_name}</td>
                        <td className="px-4 py-2 text-gray-600">
                          {CONVERSATION_STATUSES[c.status]?.label || c.status}
                        </td>
                        <td className="px-4 py-2 text-gray-600">
                          {PRIORITIES[c.priority]?.label || c.priority}
                        </td>
                        <td className="max-w-[180px] truncate px-4 py-2 text-gray-500">
                          {c.subject || '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-sm text-gray-400">Nenhuma conversa no período.</p>
            )}
          </div>
        )}
      </RowDetailModal>
    </div>
  );
}
